import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouteNavigate } from '@features/navigation/hooks/useRouteNavigate'
import { Path } from '../../../routes/paths'
import { runtimeConfig } from '../../../config/runtimeConfig'
import { ShortcutHint } from '@features/shared/components'
import GlobalSearchModal, { type SearchAnchor, type SearchSelection } from './GlobalSearchModal'
import { prefetchSearchPreview } from '../../tables/hooks/useGlobalSearch'
import { OPEN_GLOBAL_SEARCH_EVENT, type OpenGlobalSearchDetail } from '../logic/openGlobalSearch'

const FULL_SEARCH_LABEL = 'Search anything...'
const SHORT_SEARCH_LABEL = 'Search...'
const SEARCH_TRIGGER_PADDING_X = 24
const SEARCH_TRIGGER_BORDER_X = 2
const SEARCH_TRIGGER_ICON_WIDTH = 14
const SEARCH_TRIGGER_GAP = 8
const SEARCH_TRIGGER_FIT_BUFFER = 2
const SEARCH_TRIGGER_MODE_HYSTERESIS = 4
const SEARCH_POPOVER_MIN_WIDTH = 560
const SEARCH_POPOVER_VIEWPORT_PADDING = 16

type SearchTriggerDisplayMode = 'full' | 'short' | 'shortWithoutShortcut' | 'icon'

function buildSearchAnchor(triggerRect: DOMRect, centerRect: DOMRect = triggerRect): SearchAnchor {
  const viewportWidth = window.innerWidth
  const maxPopoverWidth = Math.max(1, viewportWidth - SEARCH_POPOVER_VIEWPORT_PADDING * 2)
  const width = Math.min(Math.max(triggerRect.width, SEARCH_POPOVER_MIN_WIDTH), maxPopoverWidth)
  const searchBarCenter = centerRect.left + centerRect.width / 2
  const centeredLeft = searchBarCenter - width / 2
  const maxLeft = viewportWidth - SEARCH_POPOVER_VIEWPORT_PADDING - width
  const left = Math.min(
    Math.max(centeredLeft, SEARCH_POPOVER_VIEWPORT_PADDING),
    Math.max(SEARCH_POPOVER_VIEWPORT_PADDING, maxLeft)
  )

  return {
    left,
    top: triggerRect.top,
    width,
    height: triggerRect.height,
  }
}

/**
 * Global search entry point in the app header. The collapsed trigger and the
 * open command palette share the same center: on open we measure the trigger's
 * width and the search slot's center, then render the palette centered on the
 * bar while respecting the viewport padding.
 */
export default function GlobalSearchBar() {
  const navigate = useRouteNavigate()
  const queryClient = useQueryClient()
  const [anchor, setAnchor] = useState<SearchAnchor | null>(null)
  // What the search opens with when another part of the app opens it (openGlobalSearch).
  const [initialQuery, setInitialQuery] = useState('')
  const [displayMode, setDisplayMode] = useState<SearchTriggerDisplayMode>('full')
  const displayModeRef = useRef<SearchTriggerDisplayMode>('full')
  const triggerSlotRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const fullLabelMeasureRef = useRef<HTMLSpanElement>(null)
  const shortLabelMeasureRef = useRef<HTMLSpanElement>(null)
  const shortcutMeasureRef = useRef<HTMLSpanElement>(null)
  const shortcutModifierLabel = runtimeConfig.environment.shortcutModifierLabel
  // Pointing at or focusing the trigger warms the quick preview the palette
  // opens on, so it shows at once. Nothing is fetched before then, and the
  // hydrated preview (every table's record count) waits for the open palette.
  const warmSearchPreview = useCallback(() => {
    void prefetchSearchPreview(queryClient)
  }, [queryClient])

  // Measure the trigger's box and (re)anchor the palette to it. Reused on open
  // AND on resize/scroll so the palette tracks the bar instead of drifting.
  const measure = useCallback(() => {
    const triggerRect = triggerRef.current?.getBoundingClientRect()
    const centerRect = triggerSlotRef.current?.getBoundingClientRect()
    if (!triggerRect) return
    setAnchor(buildSearchAnchor(triggerRect, centerRect))
  }, [])

  // A search opened with text typed shows its results, never the preview.
  const openSearch = useCallback(
    (query: string) => {
      setInitialQuery(query)
      if (!query.trim()) warmSearchPreview()
      measure()
    },
    [measure, warmSearchPreview]
  )

  // Cmd+K / Ctrl+K opens search from anywhere in the app.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        openSearch('')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [openSearch])

  // Another part of the app opens it with a search already typed (the
  // sidebar's search, handing over what it couldn't find).
  useEffect(() => {
    const handler = (event: Event) => {
      const { query } = (event as CustomEvent<OpenGlobalSearchDetail>).detail
      openSearch(query)
    }
    window.addEventListener(OPEN_GLOBAL_SEARCH_EVENT, handler)
    return () => window.removeEventListener(OPEN_GLOBAL_SEARCH_EVENT, handler)
  }, [openSearch])

  useLayoutEffect(() => {
    const slot = triggerSlotRef.current
    if (!slot) return

    const getMeasuredWidth = (element: HTMLElement | null) =>
      element?.getBoundingClientRect().width ?? 0

    const updateDisplayMode = () => {
      const availableWidth = slot.getBoundingClientRect().width
      const fullLabelWidth = getMeasuredWidth(fullLabelMeasureRef.current)
      const shortLabelWidth = getMeasuredWidth(shortLabelMeasureRef.current)
      const shortcutWidth = getMeasuredWidth(shortcutMeasureRef.current)
      const fullWidth =
        SEARCH_TRIGGER_BORDER_X +
        SEARCH_TRIGGER_PADDING_X +
        SEARCH_TRIGGER_ICON_WIDTH +
        SEARCH_TRIGGER_GAP +
        fullLabelWidth +
        SEARCH_TRIGGER_GAP +
        shortcutWidth +
        SEARCH_TRIGGER_FIT_BUFFER
      const shortWithShortcutWidth =
        SEARCH_TRIGGER_BORDER_X +
        SEARCH_TRIGGER_PADDING_X +
        SEARCH_TRIGGER_ICON_WIDTH +
        SEARCH_TRIGGER_GAP +
        shortLabelWidth +
        SEARCH_TRIGGER_GAP +
        shortcutWidth +
        SEARCH_TRIGGER_FIT_BUFFER
      const shortWithoutShortcutWidth =
        SEARCH_TRIGGER_BORDER_X +
        SEARCH_TRIGGER_PADDING_X +
        SEARCH_TRIGGER_ICON_WIDTH +
        SEARCH_TRIGGER_GAP +
        shortLabelWidth +
        SEARCH_TRIGGER_FIT_BUFFER

      const currentMode = displayModeRef.current
      const fullThreshold =
        fullWidth + (currentMode === 'full' ? 0 : SEARCH_TRIGGER_MODE_HYSTERESIS)
      const shortWithShortcutThreshold =
        shortWithShortcutWidth +
        (currentMode === 'full' || currentMode === 'short' ? 0 : SEARCH_TRIGGER_MODE_HYSTERESIS)
      const shortWithoutShortcutThreshold =
        shortWithoutShortcutWidth + (currentMode === 'icon' ? SEARCH_TRIGGER_MODE_HYSTERESIS : 0)

      let nextMode: SearchTriggerDisplayMode
      if (availableWidth >= fullThreshold) {
        nextMode = 'full'
      } else if (availableWidth >= shortWithShortcutThreshold) {
        nextMode = 'short'
      } else if (availableWidth >= shortWithoutShortcutThreshold) {
        nextMode = 'shortWithoutShortcut'
      } else {
        nextMode = 'icon'
      }

      if (nextMode !== currentMode) {
        displayModeRef.current = nextMode
        setDisplayMode(nextMode)
      }
    }

    updateDisplayMode()
    const observer = new ResizeObserver(updateDisplayMode)
    observer.observe(slot)
    return () => observer.disconnect()
  }, [shortcutModifierLabel])

  // While open, keep the palette aligned to the trigger as the window resizes or
  // scrolls — otherwise its width/position drifts from the bar. rAF-coalesced so
  // a burst of resize events triggers at most one re-measure per frame.
  const isOpen = anchor !== null
  useEffect(() => {
    if (!isOpen) return
    let raf = 0
    const reposition = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [isOpen, measure])

  const handleSelect = useCallback(
    (sel: SearchSelection) => {
      setAnchor(null)
      switch (sel.kind) {
        case 'record':
        case 'value':
          // Land on the record's table and open its detail panel.
          navigate(Path.tableDefaultViewRecords(sel.tableId), { state: { openRecord: sel.record } })
          break
        case 'table':
        case 'field':
          // Fields jump to their owning table's default view.
          navigate(Path.tableDefaultViewRecords(sel.tableId))
          break
        case 'view':
          // Navigate during the originating click so Ctrl/Cmd-click retains its user gesture.
          // The destination owns loading its view data just like a directly-entered URL.
          navigate(Path.tableViewRecords(sel.tableId, sel.viewId))
          break
        case 'page':
          navigate(sel.path)
          break
      }
    },
    [navigate]
  )

  return (
    <>
      <div
        ref={triggerSlotRef}
        className="flex min-w-[var(--header-control-size)] max-w-2xl flex-1 justify-center"
      >
        <button
          ref={triggerRef}
          type="button"
          onPointerEnter={warmSearchPreview}
          onPointerDown={warmSearchPreview}
          onFocus={warmSearchPreview}
          onClick={() => openSearch('')}
          aria-label="Search across everything"
          className={`app-header-control min-w-[var(--header-control-size)] shrink-0 text-left ${
            displayMode === 'icon'
              ? 'w-[var(--header-control-size)] justify-center px-0'
              : 'w-full gap-2 px-3'
          }`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
            aria-hidden="true"
          >
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <line
              x1="9.5"
              y1="9.5"
              x2="13"
              y2="13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          {displayMode !== 'icon' && (
            <span className="min-w-max flex-1 whitespace-nowrap text-sm">
              {displayMode === 'full' ? FULL_SEARCH_LABEL : SHORT_SEARCH_LABEL}
            </span>
          )}
          {(displayMode === 'full' || displayMode === 'short') && (
            <ShortcutHint
              keys={[shortcutModifierLabel, 'K']}
              className="border-default text-muted"
            />
          )}
        </button>
      </div>

      <div
        aria-hidden="true"
        className="pointer-events-none fixed -left-[9999px] top-0 flex items-center gap-2 opacity-0"
      >
        <span ref={fullLabelMeasureRef} className="whitespace-nowrap text-sm">
          {FULL_SEARCH_LABEL}
        </span>
        <span ref={shortLabelMeasureRef} className="whitespace-nowrap text-sm">
          {SHORT_SEARCH_LABEL}
        </span>
        <span ref={shortcutMeasureRef}>
          <ShortcutHint keys={[shortcutModifierLabel, 'K']} />
        </span>
      </div>

      {anchor && (
        <GlobalSearchModal
          anchor={anchor}
          onSelect={handleSelect}
          initialQuery={initialQuery}
          onClose={() => {
            setAnchor(null)
          }}
        />
      )}
    </>
  )
}
